import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/ui/Navbar";
import Footer from "../../components/ui/Footer";
import { authApi } from "../../services/api";

const CheckIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const BENEFITS = [
  "Generate and refine project topics for your department and project type.",
  "Get AI-drafted chapters — Abstract through References — in one workspace.",
  "Use diagrams and flows to explain your system clearly to supervisors.",
  "Edit, regenerate and polish text until it matches your school's format.",
];

const inputClass = (hasError) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
      : "border-slate-200 focus:border-[#0BBF64] focus:ring-[#0BBF64]/20"
  }`;

const FormField = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  placeholder,
  optional,
}) => (
  <div>
    <label htmlFor={name} className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
      {optional ? (
        <span className="ml-1 font-normal text-slate-400">(optional)</span>
      ) : (
        <span className="text-[#0BBF64]"> *</span>
      )}
    </label>
    <input
      id={name}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className={inputClass(!!error)}
      placeholder={placeholder}
    />
    {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
  </div>
);

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    referredByCode: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (apiError) setApiError("");
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (
      formData.phoneNumber.length < 11 ||
      formData.phoneNumber.length > 15
    ) {
      newErrors.phoneNumber = "Phone number must be 11 to 15 digits";
    }

    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (formData.referredByCode && formData.referredByCode.trim().length > 0) {
      const code = formData.referredByCode.trim();
      if (code.length < 3 || code.length > 32) {
        newErrors.referredByCode = "Referral code must be 3 to 32 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setApiError("");

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        ...(formData.referredByCode?.trim()
          ? { referredByCode: formData.referredByCode.trim() }
          : {}),
      };

      const response = await authApi.signup(payload);

      if (response.data && response.data.message === "successful") {
        navigate("/login", {
          state: { message: "Signup successful! Please login." },
        });
      } else {
        setApiError(
          response.data?.message || "Signup failed. Please try again."
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        "Signup failed. Please try again.";
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 lg:py-16">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 lg:items-stretch">
            {/* Marketing panel */}
            <section className="relative order-2 overflow-hidden rounded-2xl lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" />
              <div
                aria-hidden
                className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:2.5rem_2.5rem]"
              />
              <div
                aria-hidden
                className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[#0BBF64]/20 blur-3xl"
              />

              <div className="relative flex h-full flex-col justify-center p-8 sm:p-10 lg:p-12">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                  Why Astudent
                </p>
                <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                  Finish your project documentation faster
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  An AI workspace built for Nigerian students — stop staring at
                  a blank page and start submitting complete, structured work.
                </p>

                <ul className="mt-8 space-y-4">
                  {BENEFITS.map((text) => (
                    <li key={text} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-[#0BBF64]">
                        <CheckIcon />
                      </span>
                      <span className="text-sm leading-relaxed text-slate-200">
                        {text}
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="mt-8 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs leading-relaxed text-slate-400 backdrop-blur-sm">
                  No credit card required. You only pay when you are ready to
                  generate full documentation for a project.
                </p>
              </div>
            </section>

            {/* Form panel */}
            <section className="order-1 flex flex-col justify-center lg:order-2">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#0BBF64]">
                    Get started
                  </p>
                  <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                    Create your free account
                  </h1>
                </div>
                <Link
                  to="/login"
                  className="hidden text-sm text-slate-600 transition hover:text-slate-900 sm:block"
                >
                  Log in
                </Link>
              </div>

              <p className="mb-6 text-sm text-slate-600">
                Sign up in minutes. Complete your academic profile after your
                first login.
              </p>

              {apiError && (
                <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {apiError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <FormField
                    label="Full name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    placeholder="John Doe"
                  />
                  <FormField
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    placeholder="john@example.com"
                  />
                  <FormField
                    label="Phone number"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    error={errors.phoneNumber}
                    placeholder="08012345678"
                  />
                  <FormField
                    label="Referral code"
                    name="referredByCode"
                    value={formData.referredByCode}
                    onChange={handleChange}
                    error={errors.referredByCode}
                    placeholder="e.g. ASXXXXXX"
                    optional
                  />
                  <FormField
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password}
                    placeholder="At least 6 characters"
                  />
                  <FormField
                    label="Confirm password"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={errors.confirmPassword}
                    placeholder="Repeat password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-[#0BBF64] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-[#0aa459] hover:shadow-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Creating account…" : "Create account"}
                </button>

                <p className="text-center text-xs leading-relaxed text-slate-500">
                  By signing up, you agree to keep your Astudent account for
                  personal academic use and follow your school&apos;s project
                  policies.
                </p>

                <p className="text-center text-sm text-slate-600 sm:hidden">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="font-semibold text-[#0BBF64] hover:text-[#0aa459]"
                  >
                    Log in
                  </Link>
                </p>
              </form>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Signup;
